import { useEffect, useRef, useCallback } from 'react';

const STORAGE_PREFIX = 'dt-v2-col-order:';

interface UseColumnOrderPersistenceConfig {
  enabled: boolean;
  tableKey: string;
}

/**
 * Reads/writes column order to localStorage.
 *
 * On mount, loads the saved order. Provides a `save` callback that debounces
 * writes (300ms) to avoid thrashing on rapid reorder drags.
 *
 * Reconciliation: columns that no longer exist in the schema are stripped;
 * new schema columns not in the saved list are appended at the end.
 */
export function useColumnOrderPersistence(config: UseColumnOrderPersistenceConfig) {
  const { enabled, tableKey } = config;
  const storageKey = STORAGE_PREFIX + tableKey;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback((): string[] => {
    if (!enabled || !tableKey) return [];
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter((v: unknown) => typeof v === 'string') : [];
    } catch {
      return [];
    }
  }, [enabled, tableKey, storageKey]);

  const save = useCallback(
    (order: string[]) => {
      if (!enabled || !tableKey) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        try {
          if (order.length === 0) {
            localStorage.removeItem(storageKey);
          } else {
            localStorage.setItem(storageKey, JSON.stringify(order));
          }
        } catch {
          // Storage full or unavailable — silently ignore
        }
      }, 300);
    },
    [enabled, tableKey, storageKey],
  );

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return { load, save };
}

/**
 * Reconciles a saved column order against the current schema columns.
 * - Removes fields that no longer exist in the schema
 * - Appends new schema fields (not in saved order) at the end
 */
export function reconcileColumnOrder(
  savedOrder: string[],
  schemaFields: string[],
): string[] {
  if (savedOrder.length === 0) return [];
  const schemaSet = new Set(schemaFields);
  const filtered = savedOrder.filter((f) => schemaSet.has(f));
  const savedSet = new Set(filtered);
  const newFields = schemaFields.filter((f) => !savedSet.has(f));
  return [...filtered, ...newFields];
}
